import React, { useState } from 'react';
import axios from 'axios';
import './ForgotPassword.css';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './shared';

function ForgotPassword({ navigateToLogin }) {
  const { t, language } = useLanguage(); // Issue #248: Get current language for OTP email
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    setEmail(e.target.value);
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email.trim()) {
      setError(t('auth.emailRequired'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/api/forgot-password', {
        email: email.trim(),
        language: language // Issue #248: Send language for OTP email
      });

      if (response.data.success) {
        setSuccess(true);
        setMessage(response.data.message || t('forgotPassword.defaultSuccess'));
      }
    } catch (err) {
      if (err.response && err.response.data) {
        setError(err.response.data.message || t('forgotPassword.emailNotFound'));
      } else {
        setError(t('auth.systemUnavailable'));
      }
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return email.trim().length > 0;
  };

  // Success state - show confirmation message
  if (success) {
    return (
      <div className="login-container">
        {/* Navigation Bar */}
        <div className="navigation-bar">
          <h1 className="page-title">{t('forgotPassword.sentTitle')}</h1>
        </div>

        {/* Main Content */}
        <div className="main-content">
          {/* Success Message */}
          <div className="success-message-container">
            <p className="success-message">{message}</p>
            <p className="success-message-detail">
              {t('forgotPassword.checkEmail')}
            </p>
            <p className="success-message-detail">
              {t('forgotPassword.otpValid')}
            </p>
          </div>

          {/* Button */}
          <div className="button-group">
            <Button
              variant="primary"
              fullWidth
              onClick={navigateToLogin}
            >
              {t('forgotPassword.understood')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Initial state - show email input form
  return (
    <div className="login-container">
      {/* Navigation Bar */}
      <div className="navigation-bar">
        <h1 className="page-title">{t('forgotPassword.title')}</h1>
      </div>

      {/* Main Content */}
      <div className="main-content">
        {/* Instructions Header */}
        <div className="welcome-header">
          <p className="instructions-text">
            {t('forgotPassword.instructions')}
          </p>
        </div>

        {/* Forgot Password Form */}
        <form onSubmit={handleSubmit} className="login-form">
          {/* Email Field */}
          <div className="text-field-wrapper">
            <input
              type="email"
              name="email"
              id="forgot-password-email"
              value={email}
              onChange={handleChange}
              placeholder={t('auth.studentEmail')}
              className={`text-field-input ${error ? 'error' : ''}`}
              disabled={loading}
              aria-invalid={error ? 'true' : 'false'}
              aria-describedby={error ? 'forgot-password-error' : undefined}
            />
            {error && (
              <div
                id="forgot-password-error"
                className="error-message"
                role="alert"
                aria-live="polite"
              >
                {error}
              </div>
            )}
          </div>

          {/* Buttons */}
          <div className="button-group">
            {/* Send OTP Button - disabled when error is shown (Issue #134) */}
            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={!isFormValid() || loading || error}
              aria-busy={loading}
            >
              {loading ? t('forgotPassword.sending') : t('forgotPassword.sendOtp')}
            </Button>

            {/* Back to Login Button - primary style when error is shown (Issue #134) */}
            <Button
              variant={error ? 'primary' : 'secondary'}
              fullWidth
              onClick={navigateToLogin}
              disabled={loading}
            >
              {t('forgotPassword.backToLogin')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ForgotPassword;
